import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect } from "vue"
import _imports_0 from '../assets/ccc-logo-rgb.svg'


const _hoisted_1 = { class: "CCC__table" }
const _hoisted_2 = {
  key: 0,
  class: "logo",
  src: _imports_0,
  alt: ""
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { class: "table__inputs" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "sorting__arrows"
}
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = {
  key: 1,
  class: "sorting__arrows"
}
const _hoisted_13 = { key: 1 }
const _hoisted_14 = {
  key: 2,
  class: "table__footer"
}
const _hoisted_15 = { class: "pagination" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]
const _hoisted_20 = { key: 3 }

import { ref, onMounted, computed, watch } from 'vue';
import {
    FlexRender,
    getCoreRowModel,
    getSortedRowModel,
    useVueTable,
    createColumnHelper,
    columnDef,
    getPaginationRowModel,
    getFilteredRowModel
} from '@tanstack/vue-table'
import { useRoute } from 'vue-router';
import { getStockData } from '../services/GetStockData'
type StockItem = {
    sku: string,
    name: string,
    colour: string,
    size: string,
    stock: number,
    inProduction: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Table',
  setup(__props) {

const route = useRoute()

let stockData = ref<StockData[]>([])
let errorMsg = ref(null)

onMounted(async () => {

  document.title = "All Stock | Continental Clothing Company";

    try {
        const response = await getStockData();
        stockData.value = response.data;
    } catch (error) {
        errorMsg.value = "Failed to fetch stock data.";
    }
});

const stockColumns: ColumnDef<StockItem>[] = [
    {
        accessorKey: "sku",
        header: "Code",
    },
    {
        accessorKey: "name",
        header: "Name",
    },
    {
        accessorKey: "colour",
        header: "Colour",
    },
    {
        accessorKey: "size",
        header: "Size",
    },
    {
        accessorKey: "stock",
        header: "Stock",
        cell: ({ getValue }) => {
            const stock = getValue<number>();
            return stock > 0 ? stock : "Out of Stock";
        },
    },
    {
        accessorKey: "inProduction",
        header: "In Production"
    },
];

const columnHelper = createColumnHelper<StockItem>()

const data = ref(stockData.value)
const pageSize = ref(20);
const globalFilter = ref('');

// Table Instance with Pagination
const table = useVueTable({
    data: computed(() => stockData.value),
    columns: stockColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(), // activate pagination
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
        pagination: {
            pageSize: pageSize.value,
        },
        sorting: [
            {
                id: 'sku',
                desc: false
            }
        ],
        globalFilter: computed(() => globalFilter.value),
    }
});

const applyGlobalFilter = () => {
    console.log(globalFilter.value)
    console.log(table.getPrePaginationRowModel().rows)
    table.setGlobalFilter(globalFilter.value); // Apply global filter
};

// work out total pages in pagination
const totalPages = computed(() => {
    return Math.ceil(table.getPrePaginationRowModel().rows.length / pageSize.value);
});

const handleSort = (column) => {
    const isSortedAsc = column.column.getIsSorted() === 'asc';
    const newSortDirection = isSortedAsc ? 'desc' : 'asc';
    table.setSorting([{ id: column.id, desc: newSortDirection === 'desc' }]);  // Set sorting state
};

// download helper functions //
const escapeCsvValue = (value: string) => {
    if (typeof value === 'string') {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        // If the value contains commas, newlines, or double quotes, enclose it in double quotes
        if (value.includes(',') || value.includes('\n') || value.includes('\r') || value.includes('"')) {
            value = `"${value}"`;
        }
    }
    return value;
};

const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
};

const downloadCSV = (csvContent: string) => {
    const timestamp = getCurrentDateTime();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `continental-clothing-stock-data-${timestamp}.csv`; // Filename with timestamp
    link.click();
};
// download helper functions END //

const downloadAllData = () => {
    const allRows = stockData.value.sort((a, b) => a.sku.localeCompare(b.sku));  // Get all data from the source - unfiltered
    const headers = stockColumns.map(col => col.header);

    let csvContent = headers.map(escapeCsvValue).join(',') + '\n';

    allRows.forEach(row => {
        const rowData = Object.values(row).map(value => escapeCsvValue(value));
        csvContent += rowData.join(',') + '\n';
    });

    downloadCSV(csvContent);
}

const downloadFilteredData = () => {
    const allRows = table.getPrePaginationRowModel().rows; // get filtered values, ignoring pagination
    const headers = stockColumns.map(col => col.header);

    let csvContent = headers.map(escapeCsvValue).join(',') + '\n';

    allRows.forEach(row => {
        const rowData = row.getVisibleCells().map(cell => escapeCsvValue(cell.getValue()));
        csvContent += rowData.join(',') + '\n';
    });

    downloadCSV(csvContent);
}

watch(pageSize, (newSize) => {
    table.setPageSize(newSize); // Dynamically adjust table page size
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(stockData).length && _unref(route).query.embed != 'true')
      ? (_openBlock(), _createElementBlock("img", _hoisted_2))
      : _createCommentVNode("", true),
    (_unref(errorMsg))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(errorMsg)), 1))
      : (_unref(stockData).length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((globalFilter).value = $event)),
                onInput: applyGlobalFilter,
                placeholder: "Search all columns..."
              }, null, 544), [
                [_vModelText, globalFilter.value]
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("button", {
                  disabled: (_unref(table).getRowModel().rows.length == 0) ? true : false,
                  onClick: _withModifiers(downloadFilteredData, ["prevent"])
                }, "Download Search Results (CSV)", 8, _hoisted_6),
                _createElementVNode("button", {
                  onClick: _withModifiers(downloadAllData, ["prevent"])
                }, "Download All (CSV)")
              ])
            ]),
            (_unref(table).getRowModel().rows.length != 0)
              ? (_openBlock(), _createElementBlock("table", _hoisted_7, [
                  _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getHeaderGroups()[0].headers, (column) => {
                        return (_openBlock(), _createElementBlock("th", {
                          key: column.id,
                          onClick: ($event: any) => (handleSort(column))
                        }, [
                          _createElementVNode("span", null, [
                            _createElementVNode("span", null, _toDisplayString(column.column.columnDef.header), 1),
                            (column.column.getIsSorted())
                              ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                                  (column.column.getIsSorted() === 'asc')
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, "↑"))
                                    : _createCommentVNode("", true),
                                  (column.column.getIsSorted() === 'desc')
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, "↓"))
                                    : _createCommentVNode("", true)
                                ]))
                              : (_openBlock(), _createElementBlock("span", _hoisted_12, _cache[6] || (_cache[6] = [
                                  _createElementVNode("span", null, "↑", -1),
                                  _createElementVNode("span", null, "↓", -1)
                                ])))
                          ])
                        ], 8, _hoisted_8))
                      }), 128))
                    ])
                  ]),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getRowModel().rows, (row) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: row.id
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                          return (_openBlock(), _createElementBlock("td", {
                            key: cell.id
                          }, _toDisplayString(cell.getValue()), 1))
                        }), 128))
                      ]))
                    }), 128))
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_13, _cache[7] || (_cache[7] = [
                  _createElementVNode("h1", null, "No Results.", -1)
                ]))),
            (_unref(table).getRowModel().rows.length != 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _createElementVNode("div", _hoisted_15, [
                    _createElementVNode("button", {
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(table).firstPage())),
                      disabled: !_unref(table).getCanPreviousPage()
                    }, " 1 ", 8, _hoisted_16),
                    _createElementVNode("button", {
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(table).previousPage())),
                      disabled: !_unref(table).getCanPreviousPage()
                    }, _cache[8] || (_cache[8] = [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 320 512"
                      }, [
                        _createElementVNode("path", { d: "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" })
                      ], -1)
                    ]), 8, _hoisted_17),
                    _createElementVNode("span", null, " Page " + _toDisplayString(_unref(table).getState().pagination.pageIndex + 1) + " of " + _toDisplayString(totalPages.value), 1),
                    _createElementVNode("button", {
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(table).nextPage())),
                      disabled: !_unref(table).getCanNextPage()
                    }, _cache[9] || (_cache[9] = [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 320 512"
                      }, [
                        _createElementVNode("path", { d: "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" })
                      ], -1)
                    ]), 8, _hoisted_18),
                    _createElementVNode("button", {
                      onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(table).lastPage())),
                      disabled: !_unref(table).getCanNextPage()
                    }, _toDisplayString(_unref(table).getPageCount()), 9, _hoisted_19)
                  ]),
                  _withDirectives(_createElementVNode("select", {
                    class: "page__size",
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((pageSize).value = $event))
                  }, _cache[10] || (_cache[10] = [
                    _createElementVNode("option", { value: 20 }, "20 Per Page", -1),
                    _createElementVNode("option", { value: 50 }, "50 Per Page", -1),
                    _createElementVNode("option", { value: 100 }, "100 Per Page", -1)
                  ]), 512), [
                    [_vModelSelect, pageSize.value]
                  ])
                ]))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[11] || (_cache[11] = [
            _createElementVNode("svg", {
              class: "spinner",
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 512 512"
            }, [
              _createElementVNode("path", {
                class: "fa-secondary",
                opacity: ".4",
                d: "M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z"
              }),
              _createElementVNode("path", {
                class: "fa-primary",
                d: "M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z"
              })
            ], -1)
          ]))),
    _cache[12] || (_cache[12] = _createElementVNode("div", { class: "h-4" }, null, -1))
  ]))
}
}

})